/**
 * CSV Utilities for Custom Trip Event Import/Export
 */

const CSV_HEADERS = [
    'eventTypeId',
    'delay',
    'latitude',
    'longitude',
    'altitude',
    'speed',
    'heading',
    'odometer',
    'satellites',
    'hdop',
    'rssi',
    'sequence',
    'distance',
    'voltage'
];

const NUMERIC_FIELDS = [
    'delay',
    'latitude',
    'longitude',
    'altitude',
    'speed',
    'heading',
    'odometer',
    'satellites',
    'hdop',
    'rssi',
    'sequence',
    'distance',
    'voltage'
];

/**
 * Parse CSV text and convert to trip events array
 * @param {string} csvText - The raw CSV text
 * @param {Array} validEventTypes - Array of valid event type values for current device
 * @returns {object} { events: Array, errors: Array }
 */
export const parseTripsCSV = (csvText, validEventTypes = []) => {
    const errors = [];
    const events = [];

    if (!csvText || csvText.trim() === '') {
        errors.push('CSV file is empty');
        return { events: [], errors };
    }

    const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);

    if (lines.length === 0) {
        errors.push('No data found in CSV');
        return { events: [], errors };
    }

    // Parse header
    const headerLine = lines[0];
    const headers = headerLine.split(',').map(h => h.trim());

    // Validate headers
    const missingHeaders = CSV_HEADERS.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
        errors.push(`Missing required CSV columns: ${missingHeaders.join(', ')}`);
        return { events: [], errors };
    }

    // Parse data rows
    for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row.trim()) continue;

        const rowErrors = [];
        const parsedValues = parseCSVRow(row);
        // Tolerate a single trailing comma that creates one extra empty column.
        const values = (
            parsedValues.length === headers.length + 1 && parsedValues[parsedValues.length - 1] === ''
        )
            ? parsedValues.slice(0, -1)
            : parsedValues;

        if (values.length !== headers.length) {
            rowErrors.push(`Column count mismatch (expected ${headers.length}, got ${values.length})`);
        }

        // Create event object
        const event = {
            id: Date.now() + i, // Generate unique ID
            eventTypeId: '',
            delay: '',
            latitude: '',
            longitude: '',
            altitude: '',
            speed: '',
            heading: '',
            odometer: '',
            satellites: '',
            hdop: '',
            rssi: '',
            sequence: '',
            distance: '',
            voltage: ''
        };

        // Map values to headers
        for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            const value = values[j] || '';

            if (header in event) {
                // Validate eventTypeId
                if (header === 'eventTypeId') {
                    if (!value) {
                        rowErrors.push('eventTypeId is required');
                    } else if (validEventTypes.length > 0 && !validEventTypes.includes(value)) {
                        rowErrors.push(`Invalid eventTypeId: "${value}"`);
                    }
                    event[header] = value;
                } else if (NUMERIC_FIELDS.includes(header)) {
                    // Validate numeric fields - allow empty or valid numbers
                    if (value && isNaN(parseFloat(value))) {
                        rowErrors.push(`${header} must be a number, got "${value}"`);
                    }
                    event[header] = value;
                } else {
                    event[header] = value;
                }
            }
        }

        if (rowErrors.length > 0) {
            errors.push(`Row ${i + 1}: ${rowErrors.join('; ')}`);
        } else {
            events.push(event);
        }
    }

    return { events, errors };
};

/**
 * Parse a single CSV row, handling quoted values
 * @param {string} row - CSV row string
 * @returns {Array} Array of values
 */
const parseCSVRow = (row) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < row.length; i++) {
        const char = row[i];
        const nextChar = row[i + 1];

        if (char === '"') {
            if (insideQuotes && nextChar === '"') {
                current += '"';
                i++;
            } else {
                insideQuotes = !insideQuotes;
            }
        } else if (char === ',' && !insideQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    result.push(current.trim());
    return result;
};

/**
 * Convert trip events array to CSV string
 * @param {Array} events - Array of trip event objects
 * @returns {string} CSV formatted string
 */
export const eventsToCSV = (events) => {
    // Add header
    const lines = [CSV_HEADERS.join(',')];

    // Add data rows
    events.forEach(event => {
        const row = CSV_HEADERS.map(header => {
            const value = event[header] || '';
            // Escape quotes in values and wrap in quotes if needed
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
                return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        });
        lines.push(row.join(','));
    });

    return lines.join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = 'trip_events.csv') => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Get sample/template CSV for custom trips
 * @param {Array} validEventTypes - Array of valid event type options
 * @returns {string} CSV template string
 */
export const getCustomTripCSVTemplate = (validEventTypes = []) => {
    const headers = CSV_HEADERS.join(',');

    // Create sample rows with example data
    let sampleEventType = validEventTypes.length > 0 ? validEventTypes[0] : 'speedUpdate';

    const sampleRows = [
        [sampleEventType, '0', '33.606198', '-117.837003', '100', '50', '90', '1000', '12', '1.5', '-50', '1', '5', '12000'].join(','),
        [validEventTypes.length > 1 ? validEventTypes[1] : 'heartbeat', '5', '33.607198', '-117.838003', '105', '55', '95', '1050', '11', '1.6', '-48', '2', '6', '12050'].join(',')
    ];

    return [headers, ...sampleRows].join('\n');
};

